import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService, PagedRequest, PagedResponse } from '../base/base-api.service';
import type { Customer } from './customer.service';
import type { Invoice } from './invoice.service';

export interface LaunchInventoryItemDto {
  id: string;
  launchId: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  quantity: number;
  /** Staff-recorded actual quantity; omit/null until set in portal. */
  fulfilledQuantity?: number | null;
  priceAtRequest: number;
  isCompleted: boolean;
  /** Invoice that billed this line, if any. */
  invoiceId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateLaunchInvoiceRequest {
  includeFuel?: boolean | null;
  launchInventoryItemIds?: string[] | null;
}

export interface LaunchInventoryItemRequest {
  inventoryItemId: string;
  quantity: number;
  /** Staff actual; omit on customer-only updates so API preserves existing. */
  fulfilledQuantity?: number | null;
}

export interface LaunchDto {
  id: string;
  vesselId: string;
  marinaId: string;
  requestType: string;
  requestedDateTime: string;
  status: string; // REQUESTED, LAUNCHED, WITH_CLIENT, RETURNED, CANCELLED
  notes?: string;
  isStayingOvernight: boolean;
  projectedReturnDateTime?: string;

  // Fuel - Requested
  fuelRequested: boolean;
  fuelTypeRequested?: string;
  fuelAmountGallons?: number; // Requested amount
  fuelAmountDollars?: number; // Requested amount
  fuelFillToFull?: boolean;
  // Fuel - Actual (support both naming conventions)
  actualFuelGallons?: number;
  actualFuelDollars?: number;
  actualFuelAmountGallons?: number; // Alias for actualFuelGallons
  actualFuelAmountDollars?: number; // Alias for actualFuelDollars
  assignedToUserId?: string;
  completedAt?: string;
  returnPhotos?: string[]; // List of photo URLs for return inspection
  returnLocationId?: string; // Dry dock location where vessel was returned
  returnLocationName?: string; // Display name for return location (resolved from ReturnLocationId)
  returnIsTemporaryLocation?: boolean; // True if returned to temporary location (not default)
  returnDefaultLocationId?: string; // Default location ID (for tracking if displaced)
  returnNotes?: string; // Staff notes / client requests recorded at return
  returnConditionIssues?: boolean; // true = issues noted; false/null = Good
  returnTime?: string; // Wall-clock return time entered by staff (HH:mm)
  createdBy?: string;
  createdAt: string;
  updatedAt: string;

  // Workflow tracking
  launchedAt?: string;
  launchedBy?: string;
  handedOverAt?: string;
  handedOverBy?: string;
  returnedAt?: string;
  returnedBy?: string;
  dryDockLocationReturnedId?: string;

  // Billing
  isBilled: boolean;
  generatedInvoiceId?: string;
  fuelInvoiceId?: string | null;
  /** Set on list/board payloads — avoids per-row invoice GETs. */
  invoiceBalanceRemaining?: number | null;
  /** Display invoice # from the same batch as balance (no extra invoice API per row). */
  invoiceNumber?: string | null;

  vessel?: any; // VesselDto if needed
  inventoryItems?: LaunchInventoryItemDto[];
}

export interface CreateLaunchRequest {
  vesselId: string;
  requestType: string;
  requestedDateTime: string;
  notes?: string;
  isStayingOvernight?: boolean;
  projectedReturnDateTime?: string;
  fuelRequested?: boolean;
  fuelTypeRequested?: string;
  fuelAmountGallons?: number;
  fuelAmountDollars?: number;
  fuelFillToFull?: boolean;
  inventoryItems?: LaunchInventoryItemRequest[];
}

export interface UpdateLaunchRequest {
  status?: string; // REQUESTED, LAUNCHED, WITH_CLIENT, RETURNED, CANCELLED
  requestedDateTime?: string;
  notes?: string;
  assignedToUserId?: string;
  completedAt?: string;
  
  // Overnight stay
  isStayingOvernight?: boolean;
  projectedReturnDateTime?: string;
  
  // Fuel - Requested
  fuelRequested?: boolean;
  fuelTypeRequested?: string;
  fuelAmountGallons?: number;
  fuelAmountDollars?: number;
  fuelFillToFull?: boolean;
  
  // Inventory items
  inventoryItems?: LaunchInventoryItemRequest[];
  
  // Workflow tracking
  updatedBy?: string;
  actualFuelGallons?: number;
  actualFuelDollars?: number;
  actualFuelAmountGallons?: number; // Alias for actualFuelGallons
  actualFuelAmountDollars?: number; // Alias for actualFuelDollars
  dryDockLocationReturnedId?: string;
  
  // Return information
  returnPhotos?: string[]; // List of photo URLs for return inspection
  returnLocationId?: string; // Dry dock location where vessel was returned
  returnIsTemporaryLocation?: boolean; // True if returned to temporary location (not default)
  returnDefaultLocationId?: string; // Default location ID (for tracking if displaced)
  returnNotes?: string;
  returnConditionIssues?: boolean;
  returnTime?: string;
}

export interface UploadReturnPhotoResponse {
  photoUrl: string;
  returnPhotos: string[];
}

export interface LaunchFilterRequest extends PagedRequest {
  marinaId?: string;
  status?: string; // ALL, REQUESTED, LAUNCHED, WITH_CLIENT, RETURNED, CANCELLED
  requestType?: string; // ALL, launch, retrieval
  /** Inclusive start date filter for requestedDateTime (YYYY-MM-DD or ISO string). */
  startDate?: string;
  /** Inclusive end date filter for requestedDateTime (YYYY-MM-DD or ISO string). */
  endDate?: string;
}

/** Staff launch detail modal: launch + customer + invoice in one HTTP call. */
export interface LaunchDetailBundleDto {
  launch: LaunchDto;
  customer?: Customer | null;
  invoice?: Invoice | null;
  invoices?: Invoice[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class LaunchService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }

  /**
   * Get launch by ID
   */
  getById(id: string): Observable<LaunchDto> {
    return this.get<LaunchDto>(`launches/${id}`);
  }

  /**
   * Launch + customer + generated invoice (staff launch board detail modal).
   */
  getDetailBundle(id: string): Observable<LaunchDetailBundleDto> {
    return this.get<LaunchDetailBundleDto>(`launches/${id}/detail`);
  }

  /**
   * Dock board snapshot for the current marina (X-Marina-Id header).
   * Active launches plus RETURNED/CANCELLED from today (UTC); not the full historical list.
   */
  getDockBoardForCurrentMarina(): Observable<LaunchDto[]> {
    return this.get<LaunchDto[]>(`launches`);
  }

  /**
   * @deprecated Use getDockBoardForCurrentMarina — marina is taken from headers only.
   */
  getByMarinaId(_marinaId: string): Observable<LaunchDto[]> {
    return this.getDockBoardForCurrentMarina();
  }

  /**
   * Paged/filtered launches for the current marina (header only).
   */
  getByMarinaIdFiltered(_marinaId: string, request?: LaunchFilterRequest): Observable<PagedResponse<LaunchDto>> {
    let params = this.getPagedParams(request);

    if (request?.status) {
      params = params.set('status', request.status);
    }
    if (request?.requestType) {
      params = params.set('requestType', request.requestType);
    }
    if (request?.startDate) {
      params = params.set('startDate', request.startDate);
    }
    if (request?.endDate) {
      params = params.set('endDate', request.endDate);
    }

    return this.get<PagedResponse<LaunchDto>>(`launches/filtered`, params);
  }

  /**
   * Get all launches for a vessel
   */
  getByVesselId(vesselId: string): Observable<LaunchDto[]> {
    return this.get<LaunchDto[]>(`launches/vessel/${vesselId}`);
  }

  /**
   * Get launches by status
   */
  getByStatus(status: string, marinaId?: string): Observable<LaunchDto[]> {
    const params = marinaId ? `?marinaId=${marinaId}` : '';
    return this.get<LaunchDto[]>(`launches/status/${status}${params}`);
  }

  /**
   * Get launches by date range
   */
  getByDateRange(startDate: string, endDate: string, marinaId?: string): Observable<LaunchDto[]> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (marinaId) {
      params.append('marinaId', marinaId);
    }
    return this.get<LaunchDto[]>(`launches/date-range?${params.toString()}`);
  }

  /**
   * Create a new launch request
   */
  create(request: CreateLaunchRequest): Observable<LaunchDto> {
    return this.post<LaunchDto>('launches', request);
  }

  /**
   * Update a launch request
   */
  update(id: string, request: UpdateLaunchRequest): Observable<LaunchDto> {
    return this.put<LaunchDto>(`launches/${id}`, request);
  }

  /**
   * Create an invoice from this launch's confirmed actual fuel and fulfilled provision amounts.
   * Estimated/requested amounts alone cannot be invoiced.
   */
  generateInvoice(id: string, request?: GenerateLaunchInvoiceRequest): Observable<Invoice> {
    return this.post<Invoice>(`launches/${id}/invoice`, request ?? {});
  }

  /**
   * Delete a launch request
   */
  delete(id: string): Observable<void> {
    return this.deleteRequest(`launches/${id}`);
  }

  /**
   * Upload a return photo for a launch
   */
  uploadReturnPhoto(id: string, file: File): Observable<UploadReturnPhotoResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    // For FormData, we need to let the browser set Content-Type with boundary
    // BaseApiService.post() handles this automatically
    return this.post<UploadReturnPhotoResponse>(`launches/${id}/upload-return-photo`, formData);
  }

  /**
   * Download a return inspection photo as a Blob via the API proxy.
   * Uses the authenticated HttpClient so the Bearer token is included automatically.
   */
  getReturnPhotoBlob(launchId: string, index: number): Observable<Blob> {
    const url = `${this.baseUrl}/launches/${launchId}/return-photos/${index}`;
    return this.http.get(url, { responseType: 'blob' });
  }
}
